/**
 * Time and business-day primitives.
 *
 * Specification: ../../test/time.spec.ts. Master plan §2 D3.
 *
 * Two rules, both learned the hard way from the pre-rewrite system:
 *   1. Never write down a UTC offset. Compute it from the IANA zone at the
 *      instant in question. Prague is +01:00 for five months of the year.
 *   2. A restaurant's day ends at business_day_start (04:00-06:00 local), not
 *      at midnight. Sales and labour must derive business_date the same way or
 *      every per-day metric is quietly wrong.
 *
 * All arithmetic here is on wall-clock parts obtained from Intl, which is what
 * makes it correct across DST without special-casing transitions.
 */

/** A venue's clock configuration. Both fields come from venue settings. */
export interface VenueClock {
  /** IANA timezone identifier, e.g. "Europe/Prague". */
  readonly timezone: string;
  /**
   * Minutes after local midnight at which the business day rolls over.
   * 04:00 → 240. A venue that closes before midnight may use 0.
   */
  readonly businessDayStartMinutes: number;
}

export interface WallTime {
  /** Local calendar date, "YYYY-MM-DD". */
  readonly date: string;
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
  /** Offset from UTC in minutes at this instant, e.g. 60 or 120 for Prague. */
  readonly offsetMinutes: number;
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = partsCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsCache.set(timeZone, f);
  }
  return f;
}

interface RawParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function rawPartsAt(instant: Date, timeZone: string): RawParts {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type);
    if (!found) throw new Error(`time: Intl did not return a ${type} part for ${timeZone}`);
    return Number(found.value);
  };
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    // Some engines render midnight as hour 24 under hour12:false.
    hour: get("hour") % 24,
    minute: get("minute"),
    second: get("second"),
  };
}

/**
 * Offset from UTC, in minutes, that `timeZone` was observing at `instant`.
 * Positive east of Greenwich. Computed, never assumed.
 */
export function offsetMinutesAt(instant: Date, timeZone: string): number {
  const p = rawPartsAt(instant, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Discard sub-second so the difference is a whole number of minutes.
  const truncated = Math.floor(instant.getTime() / 1000) * 1000;
  return Math.round((asIfUtc - truncated) / 60_000);
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

const isoDate = (year: number, month: number, day: number): string =>
  `${year}-${pad2(month)}-${pad2(day)}`;

/** Render an instant as local wall-clock parts in the given zone. */
export function localWallTime(instant: Date, timeZone: string): WallTime {
  const p = rawPartsAt(instant, timeZone);
  return {
    date: isoDate(p.year, p.month, p.day),
    year: p.year,
    month: p.month,
    day: p.day,
    hour: p.hour,
    minute: p.minute,
    second: p.second,
    offsetMinutes: offsetMinutesAt(instant, timeZone),
  };
}

function parseIsoDate(date: string): { year: number; month: number; day: number } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`time: expected YYYY-MM-DD, got ${JSON.stringify(date)}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/**
 * Convert a local wall-clock time to the UTC instant it denotes.
 *
 * DST makes this a partial function, so both edge cases are resolved
 * deterministically rather than thrown, because a scheduled shift or
 * reservation still has to land somewhere and two replicas must agree:
 *
 *   - Spring forward (the requested time never occurs): resolves to the
 *     instant the clock actually reached, i.e. the request shifted forward by
 *     the size of the jump.
 *   - Autumn back (the requested time occurs twice): resolves to the FIRST
 *     occurrence, i.e. still on the pre-transition offset.
 */
export function zonedWallTimeToInstant(
  date: string,
  hour: number,
  minute: number,
  timeZone: string,
  second = 0,
): Date {
  const { year, month, day } = parseIsoDate(date);
  const naive = Date.UTC(year, month - 1, day, hour, minute, second);

  // Sample the offset well before and well after the requested time. Any DST
  // transition near it falls between these two samples, so the pair brackets
  // every offset the zone could plausibly be observing. (12h is ample: real
  // transitions are 1h and zones with offsets beyond ±12h do not observe DST.)
  const HALF_DAY_MS = 12 * 3_600_000;
  const offsetBefore = offsetMinutesAt(new Date(naive - HALF_DAY_MS), timeZone);
  const offsetAfter = offsetMinutesAt(new Date(naive + HALF_DAY_MS), timeZone);

  const candidates =
    offsetBefore === offsetAfter
      ? [naive - offsetBefore * 60_000]
      : [naive - offsetBefore * 60_000, naive - offsetAfter * 60_000];

  // A candidate is real only if it renders back to exactly the wall time asked
  // for, on the date asked for.
  const real = candidates.filter((ts) => {
    const back = rawPartsAt(new Date(ts), timeZone);
    return (
      back.hour === hour &&
      back.minute === minute &&
      isoDate(back.year, back.month, back.day) === date
    );
  });

  // Ambiguous (autumn back, two real candidates): take the FIRST occurrence.
  if (real.length > 0) return new Date(Math.min(...real));

  // Non-existent (spring forward, no real candidate): take the LATER instant,
  // which is the wall time shifted forward by the size of the gap — the point
  // the clock actually reached.
  return new Date(Math.max(...candidates));
}

/** Add days to a "YYYY-MM-DD" string in the proleptic Gregorian calendar. */
export function addDays(date: string, days: number): string {
  const { year, month, day } = parseIsoDate(date);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return isoDate(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/**
 * The business date an instant belongs to.
 *
 * Operates purely on local wall time, which is what makes it DST-correct: an
 * instant is placed on a business day by where the clock on the wall said it
 * was, exactly as the staff experienced it.
 */
export function businessDateOf(instant: Date, clock: VenueClock): string {
  const w = localWallTime(instant, clock.timezone);
  const minutesOfDay = w.hour * 60 + w.minute;
  return minutesOfDay < clock.businessDayStartMinutes ? addDays(w.date, -1) : w.date;
}

/**
 * The half-open UTC interval [startUtc, endUtc) covering a business date.
 *
 * The span is 23, 24 or 25 hours depending on whether it contains a DST
 * transition. Any caller that assumes 24 is wrong twice a year.
 */
export function businessDayRange(
  businessDate: string,
  clock: VenueClock,
): { startUtc: Date; endUtc: Date } {
  const startHour = Math.floor(clock.businessDayStartMinutes / 60);
  const startMinute = clock.businessDayStartMinutes % 60;

  const startUtc = zonedWallTimeToInstant(businessDate, startHour, startMinute, clock.timezone);
  const endUtc = zonedWallTimeToInstant(
    addDays(businessDate, 1),
    startHour,
    startMinute,
    clock.timezone,
  );

  return { startUtc, endUtc };
}

/**
 * The triple every fact in the system carries. Storing all three means a
 * report can ask "what did the staff see?" and "what actually happened, in
 * order?" without re-deriving either.
 */
export interface Stamped {
  readonly occurredAtUtc: Date;
  readonly occurredAtLocal: string;
  readonly businessDate: string;
}

export function stamp(instant: Date, clock: VenueClock): Stamped {
  const w = localWallTime(instant, clock.timezone);
  return {
    occurredAtUtc: instant,
    occurredAtLocal: `${w.date}T${pad2(w.hour)}:${pad2(w.minute)}:${pad2(w.second)}`,
    businessDate: businessDateOf(instant, clock),
  };
}

// --- Attributing an event to a trading day ---------------------------------

/**
 * How far ahead of the server a device may be before its clock is disbelieved.
 *
 * Generous enough to absorb ordinary drift and the flight time of a request,
 * tight enough that a genuinely wrong clock is caught on the first event.
 */
const FUTURE_TOLERANCE_MS = 2 * 60_000;

export interface Attribution {
  readonly businessDate: string;
  /**
   * The device's clock was not believable. The event is still stored — refusing
   * it would discard a real sale — but the divergence is on the row.
   */
  readonly skewFlagged: boolean;
}

/**
 * Decide which trading day an event belongs to, and whether to believe its
 * timestamp.
 *
 * Specification: ../../test/time.spec.ts. Master plan §3 K3, §2 D2.
 *
 * Ingest used to stamp every event with the day it was RECEIVED. For an online
 * till that is indistinguishable from the right answer, which is why it
 * survived — the assertion that the date comes from the venue and never from
 * the device stayed true the whole time, and it was the wrong question.
 *
 * THIS PLATFORM IS BUILT AROUND TILLS THAT ARE OFFLINE. A device that traded on
 * Friday and drained on Monday filed Friday's sales under Monday, so the
 * venue's Friday Z-report and the event log disagreed permanently, and nothing
 * could recover it: the only record of when the sale happened had been
 * overwritten by when the network came back.
 *
 * WHAT THIS CAN AND CANNOT DECIDE is the whole design.
 *
 * A FUTURE timestamp is IMPOSSIBLE — no clock legitimately predicts — so it is
 * flagged and attribution falls back to server time. A sale must not create a
 * trading day that has not happened, because nobody will ever reconcile it and
 * it sits in the reports forever.
 *
 * A PAST timestamp is ALWAYS POSSIBLE, and nothing here can tell a four-day
 * offline drain from a void backdated to before the payment it conceals: both
 * are an old timestamp arriving now. So it does not guess. It attributes to the
 * device's clock, because that is the only record of when the sale happened,
 * and leaves the improbable to a detector with more context — the cohort
 * statistics in @gf/module-cash have the machinery for exactly that.
 *
 * Flagging every offline drain would not be conservative. It would set the
 * column on every event a seasonal venue ever sent, and a signal that fires
 * constantly is one nobody reads.
 */
export function attributeBusinessDate(args: {
  occurredAt: Date;
  receivedAt: Date;
  clock: VenueClock;
}): Attribution {
  const occurred = args.occurredAt.getTime();

  // A malformed date reaching `businessDateOf` yields an Invalid Date business
  // date, which Postgres refuses — a 500 for one bad event that takes the whole
  // batch with it.
  if (Number.isNaN(occurred)) {
    return { businessDate: businessDateOf(args.receivedAt, args.clock), skewFlagged: true };
  }

  if (occurred > args.receivedAt.getTime() + FUTURE_TOLERANCE_MS) {
    return { businessDate: businessDateOf(args.receivedAt, args.clock), skewFlagged: true };
  }

  return { businessDate: businessDateOf(args.occurredAt, args.clock), skewFlagged: false };
}
